/**
 * @typedef {Object} PublicPlanAnnualDiscount
 * @property {boolean} enabled
 * @property {number} savingsPercent
 * @property {'percentage' | 'fixed_amount' | null} [discountType]
 * @property {number | null} [discountValue]
 * @property {string | null} [discountLabel]
 */

/**
 * @typedef {Object} PublicPricingPlan
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {number} monthlyPrice
 * @property {number} annualPrice
 * @property {number} [annualTotal]
 * @property {number} [annualListPrice]
 * @property {PublicPlanAnnualDiscount} [annualDiscount]
 * @property {string[]} features
 * @property {boolean} popular
 */

/**
 * @typedef {Object} PublicPlansPayload
 * @property {PublicPricingPlan[]} plans
 * @property {number} annualSavingsPercent
 * @property {number} annualSavingsPercentMin
 * @property {boolean} hasVariableAnnualSavings
 */

export function buildAnnualToggleSavingsLabel({
  annualSavingsPercent = 0,
  hasVariableAnnualSavings = false,
} = {}) {
  if (!annualSavingsPercent) return 'Annual billing';
  if (hasVariableAnnualSavings) return `Up to ${annualSavingsPercent}% off`;
  return `Save ${annualSavingsPercent}%`;
}

export function planHasAnnualSavings(plan) {
  if (plan?.annualDiscount?.enabled) return true;
  const monthly = Number(plan?.monthlyPrice) || 0;
  const annual = Number(plan?.annualPrice) || 0;
  return annual > 0 && annual < monthly;
}

export function buildPlanAnnualSavingsLabel(plan) {
  const discount = plan?.annualDiscount;
  if (discount?.discountLabel) return discount.discountLabel;
  if (discount?.enabled && discount.savingsPercent > 0) {
    return `Save ${discount.savingsPercent}%`;
  }
  const inferred = inferSavingsPercentFromPrices(plan?.monthlyPrice, plan?.annualPrice);
  if (inferred > 0) return `Save ${inferred}%`;
  return null;
}

export function inferSavingsPercentFromPrices(monthlyPrice, annualPrice) {
  const monthly = Number(monthlyPrice) || 0;
  const annual = Number(annualPrice) || 0;
  if (monthly <= 0 || annual >= monthly) return 0;
  return Math.round(((monthly - annual) / monthly) * 100);
}
